import React, { useEffect, useState } from 'react';
import Overlay from './Overlay.jsx';
import { num, partRowKey } from './palletCheckData';
import { C, card, sectionTitle, tab } from './styles';

// The parts on the pallet, ONE row per (part, assembly) (mirror assemblies and spare legs next to stands
// stay apart): a picture you can tap (the part's picture on file, usually its drawing render, else the shape
// from the DXF outline), units packed of planned (assemblies for an assembly row) and a bar. Tapped: the
// shape large with its size, thickness and weight, and a Picture / Shape toggle when both exist.
// No upload from here: the part's picture is on routing slips, labels and a customer PDF (review 2026-10-23).

const isAssemblyRow = (p) => p.assembly_id != null;

function Thumb({ part, size = 72 }) {
  const src = part.image_url || part.outline_svg_url;
  const [broken, setBroken] = useState(null); // the src that failed to load
  if (!src || broken === src) {
    return <i className="fas fa-cube" aria-hidden="true" style={{ fontSize: size / 2.4, color: C.steel }} />;
  }
  return <img src={src} alt="" onError={() => setBroken(src)} style={{ maxWidth: size - 10, maxHeight: size - 10, objectFit: 'contain' }} />;
}

/** "0.1196 in thick · 1.22 lb each" (an assembly row: its weight per assembly). */
const detailText = (part) => (isAssemblyRow(part)
  ? [`${part.product_id} assembly`, part.unit_weight_lbs ? `${num(part.unit_weight_lbs, 1)} lb each` : null]
  : [part.thickness_in ? `${num(part.thickness_in, 4)} in thick` : null,
    (part.unit_weight_lbs ?? part.weight_lbs) ? `${num(part.unit_weight_lbs ?? part.weight_lbs, 2)} lb each` : null]
).filter(Boolean).join(' · ');

/** "assemblies" / "assembly" / the order's unit word. */
const unitsWord = (part, n) => (isAssemblyRow(part) ? `assembl${n === 1 ? 'y' : 'ies'}` : 'pcs');

function PartCard({ part, onOpen }) {
  const key = partRowKey(part);
  const asm = isAssemblyRow(part);
  const title = asm ? part.assembly_name || part.product_id : part.product_id;
  const planned = Number(part.units_planned) || 0;
  const packed = Number(part.units_packed) || 0;
  const pctDone = planned > 0 ? Math.min(100, Math.round((packed / planned) * 100)) : 0;
  // weight_lbs_total, NEVER units x weight_lbs (an assembly weighs far more than its primary part).
  const steel = part.weight_lbs_total != null ? `${num(part.weight_lbs_total, 1)} lb` : '—';
  const jobs = (part.jobs || []).map((j) => `Job #${j.master_job_num ?? j.job_id}`).join(', ') || '—';
  const short = Number(part.short_units) || 0;
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }} data-testid={`part-${key}`}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="button" onClick={() => onOpen(part)} aria-label={`Enlarge the ${title} picture`}
          style={{ flexShrink: 0, width: 72, height: 72, background: '#F7F7F4', border: `1px solid ${C.dim}`, borderRadius: 10, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer', overflow: 'hidden' }}>
          <Thumb part={part} />
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1, minWidth: 0 }}>
          <div style={{ fontFamily: asm ? 'inherit' : C.mono, fontSize: 17, fontWeight: 700, overflowWrap: 'anywhere' }} data-testid={`part-title-${key}`}>{title}</div>
          <div style={{ fontSize: 15 }}>{part.description}</div>
          <div style={{ fontSize: 13, color: C.sub }}>{detailText(part)}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 700 }} data-testid={`part-packed-${key}`}>{packed}</div>
          <div style={{ fontSize: 13, color: C.sub }} data-testid={`part-planned-${key}`}>of {planned} {unitsWord(part, planned)}</div>
        </div>
      </div>
      <div style={{ height: 10, background: C.track, borderRadius: 999, overflow: 'hidden' }}
        role="progressbar" aria-label={`${title} packed`} aria-valuemin={0} aria-valuemax={planned} aria-valuenow={packed}>
        <div style={{ width: `${pctDone}%`, height: 10, background: pctDone >= 100 ? C.green : C.amber }} />
      </div>
      {short > 0 && <div style={{ fontSize: 14, fontWeight: 700, color: C.red }}>Short {short} {unitsWord(part, short)}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, fontSize: 13 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ color: C.muted }}>{(part.containers || []).length > 1 ? 'Where' : 'Stack / box'}</span><b>{(part.containers || []).join(', ') || '—'}</b></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ color: C.muted }}>Steel weight</span><b data-testid={`part-weight-${key}`}>{steel}</b></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ color: C.muted }}>Cut on</span><b>{jobs}</b></div>
      </div>
    </div>
  );
}

export function PartLightbox({ open, onClose, part, palletShortId, noun = 'pallet' }) {
  const [view, setView] = useState('picture');
  const rowKey = part ? partRowKey(part) : null;
  const startView = part && part.image_url ? 'picture' : 'shape';
  useEffect(() => {
    if (open && rowKey) setView(startView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowKey]);
  if (!open || !part) return null;

  const asm = isAssemblyRow(part);
  const title = asm ? part.assembly_name || part.product_id : part.product_id;
  const hasPicture = !!part.image_url;
  const hasShape = !!part.outline_svg_url;
  const size = (v) => (v ? `${Number(v).toFixed(2)} in` : '');
  const each = part.unit_weight_lbs ?? (asm ? null : part.weight_lbs);
  const n = Number(part.units_planned) || 0;

  return (
    <Overlay open={open} onClose={onClose} title={title}
      subtitle={`${asm ? `${part.product_id} assembly` : part.description || ''}${palletShortId ? ` · on ${palletShortId}` : ''}`} testId="part-lightbox">
      {hasPicture && hasShape && (
        <div style={{ padding: '0 16px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }} role="tablist">
          <button type="button" role="tab" aria-selected={view === 'picture'} style={tab(view === 'picture')} onClick={() => setView('picture')}>Picture</button>
          <button type="button" role="tab" aria-selected={view === 'shape'} style={tab(view === 'shape')} onClick={() => setView('shape')}>Shape</button>
        </div>
      )}
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 16 }}>
        {(view === 'picture' && hasPicture) || (hasPicture && !hasShape) ? (
          <img src={part.image_url} alt={`${title}, as on file`} style={{ maxWidth: '100%', maxHeight: '55vh', borderRadius: 6, background: '#F7F7F4' }} />
        ) : hasShape ? (
          <>
            <div style={{ fontSize: 14, color: C.dim }}>{size(part.size_length)}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 280, maxWidth: '75vw', aspectRatio: '1 / 1', background: '#F7F7F4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={part.outline_svg_url} alt={`Shape of ${part.product_id}, from its DXF`} style={{ width: '92%', height: '92%', objectFit: 'contain' }} />
              </div>
              <div style={{ writingMode: 'vertical-rl', fontSize: 14, color: C.dim }}>{size(part.size_width)}</div>
            </div>
            <div style={{ fontSize: 14, color: C.dim }}>Seen from above, as nested on the cut sheet{asm ? ` (the ${part.product_id} part)` : ''}</div>
          </>
        ) : (
          <div style={{ fontSize: 15, color: C.dim }}>No picture or shape on file for this part.</div>
        )}
      </div>
      <div style={{ margin: '0 16px', background: C.panel, borderRadius: 12, padding: 14, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, fontSize: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ color: C.dim }}>Size</span><b style={{ fontSize: 16 }}>{part.size_length && part.size_width ? `${num(part.size_length, 2)} × ${num(part.size_width, 2)} in` : '—'}</b></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ color: C.dim }}>Thickness</span><b style={{ fontSize: 16 }}>{part.thickness_in ? `${num(part.thickness_in, 4)} in` : '—'}</b></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ color: C.dim }}>{asm ? 'Each assembly weighs' : 'Each weighs'}</span><b style={{ fontSize: 16 }}>{each ? `${num(each, 2)} lb` : '—'}</b></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ color: C.dim }}>On this {noun}</span><b style={{ fontSize: 16 }}>{part.unit_word || `${n} ${unitsWord(part, n)}`}</b></div>
      </div>
      <div style={{ padding: '12px 16px 18px', display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 0, color: '#93C5FD', fontFamily: 'inherit', fontSize: 15, minHeight: 44, cursor: 'pointer' }}>Back to the {noun}</button>
      </div>
    </Overlay>
  );
}

export default function PartsList({ parts, onOpen, noun = 'pallet' }) {
  const list = parts || [];
  return (
    <div style={{ padding: '24px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h2 style={sectionTitle}>Parts on this {noun} ({list.length})</h2>
      {list.length === 0 && <div style={{ ...card, fontSize: 15 }}>No parts on this {noun}.</div>}
      {list.map((p) => <PartCard key={partRowKey(p)} part={p} onOpen={onOpen} />)}
    </div>
  );
}
